import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import VideoPlayer from './VideoPlayer';
import Notes from './Notes';
import { useUI } from '../../../../contexts/UIContext';
import { 
  Card as BaseCard, 
  IconButton 
} from '../../../../styles/components';
import { glassDeep } from '../../../../styles/components/common/glassmorphism';
import { fadeInAnimation, scaleInAnimation } from '../../../../styles/components/common/animations';

const PanelOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.95);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  animation: ${fadeInAnimation} 0.3s ease-out;
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textOnDark};
  ${glassDeep}
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textOnDark};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const CloseButton = styled(IconButton)`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textOnDark};
  margin-left: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.textOnDark};
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  height: calc(100vh - 64px);
  overflow: hidden;
  animation: ${scaleInAnimation} 0.3s ease-out;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  min-height: 0;
`;

const ChaptersList = styled(BaseCard)`
  ${glassDeep}
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const ChapterItem = styled.button`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.lg}`};
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .time {
    color: ${({ theme }) => theme.colors.primaryLight};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    min-width: 4rem;
  }
  
  .title {
    color: ${({ theme }) => theme.colors.textOnDark};
  }
  
  &.current {
    background: rgba(99, 102, 241, 0.2);
    
    .time, .title {
      color: ${({ theme }) => theme.colors.primaryLight};
    }
  }
`;

const NotesSection = styled.div`
  height: 100%;
  overflow: hidden;
`;

const PreRecordedPanel = ({ workshop, onClose }) => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const videoRef = React.useRef(null);
  const { setWorkshopModalOpen } = useUI();
  
  useEffect(() => {
    // Set modal open when component mounts
    setWorkshopModalOpen(true);
    
    // Set modal closed when component unmounts
    return () => setWorkshopModalOpen(false);
  }, [setWorkshopModalOpen, workshop.id]);
  
  const handleChapterClick = (index, time) => {
    setCurrentChapter(index);
    if (videoRef.current) {
      const [minutes, seconds] = time.split(':').map(Number);
      videoRef.current.currentTime = minutes * 60 + seconds;
      videoRef.current.play();
    }
  };
  
  return (
    <PanelOverlay>
      <Header>
        <Title>{workshop.title}</Title>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </Header>
      
      <Content>
        <MainSection>
          <VideoPlayer
            ref={videoRef}
            src={workshop.videoUrl}
            poster={workshop.thumbnailUrl}
          />
          
          {workshop.chapters && (
            <ChaptersList>
              {workshop.chapters.map((chapter, index) => (
                <ChapterItem
                  key={index}
                  className={currentChapter === index ? 'current' : ''}
                  onClick={() => handleChapterClick(index, chapter.time)}
                >
                  <span className="time">{chapter.time}</span>
                  <span className="title">{chapter.title}</span>
                </ChapterItem>
              ))}
            </ChaptersList>
          )}
        </MainSection>
        
        <NotesSection>
          <Notes
            workshopId={workshop.id}
            onSave={notes => {
              // In a real app, this would save to a backend
              console.log('Saving notes:', notes);
            }}
          />
        </NotesSection>
      </Content>
    </PanelOverlay>
  );
};

export default PreRecordedPanel;
